import NotificationsIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type FC, type MouseEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import { usePermissions } from "src/auth/usePermissions";
import { NotificationRow } from "src/notifications/NotificationRow";
import {
	type NotificationItem,
	useNotificationActions,
	useNotificationList,
	useUnreadNotifications
} from "src/notifications/useNotifications";
import { ROUTES } from "src/routes/paths";

/** The latest few; the rest are on Activity. */
const BELL_LIST_SIZE = 8;
const POPOVER_WIDTH = 380;
const POPOVER_MAX_HEIGHT = 480;
/** The Figma bell (2:8059) is 20px beside a 36px avatar. */
const BELL_ICON_SIZE = 22;
const BADGE_MAX = 99;

/**
 * The top bar's bell (Figma "Notification + Account" 2:8059; on phones "Activity lives behind the
 * bell"): the unread count, and a popover of the latest notifications with Activity and settings.
 */
export const NotificationBell: FC = () => {
	const { t } = useTranslation();
	const { canReadNotifications } = usePermissions();
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const summary = useUnreadNotifications();
	const list = useNotificationList({ limit: BELL_LIST_SIZE }, Boolean(anchor));
	const { open, markAllRead } = useNotificationActions();
	const unread = summary.data?.unread ?? 0;

	const openPopover = useCallback((event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget), []);
	const closePopover = useCallback(() => setAnchor(null), []);
	const handleOpen = useCallback(
		async (notification: NotificationItem) => {
			setAnchor(null);
			await open(notification);
		},
		[open]
	);
	const handleMarkAllRead = useCallback(() => markAllRead.mutate(undefined), [markAllRead]);

	if (!canReadNotifications) return null;

	return (
		<>
			<IconButton
				onClick={openPopover}
				aria-label={t("notifications.bell", { count: unread })}
				aria-haspopup="dialog"
				sx={{ p: 0.75 }}>
				<Badge badgeContent={unread} max={BADGE_MAX} color="error">
					<NotificationsIcon color="primary" sx={{ fontSize: BELL_ICON_SIZE }} />
				</Badge>
			</IconButton>
			<Popover
				open={Boolean(anchor)}
				anchorEl={anchor}
				onClose={closePopover}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{
					paper: {
						role: "dialog",
						"aria-label": t("notifications.title"),
						sx: {
							width: POPOVER_WIDTH,
							maxWidth: "calc(100vw - 32px)",
							maxHeight: `min(${POPOVER_MAX_HEIGHT}px, calc(100dvh - 32px))`
						}
					}
				}}>
				<Stack direction="row" sx={{ alignItems: "center", px: 2, pt: 1.5, pb: 1, gap: 1 }}>
					<Typography variant="subtitle1" component="h2" sx={{ flex: 1, fontWeight: 600 }}>
						{t("notifications.title")}
					</Typography>
					<Button size="small" onClick={handleMarkAllRead} disabled={unread === 0 || markAllRead.isPending}>
						{t("notifications.markAllRead")}
					</Button>
					<IconButton
						component={RouterLink}
						to={ROUTES.notificationSettings}
						onClick={closePopover}
						aria-label={t("notifications.settings.title")}
						size="small">
						<SettingsIcon fontSize="small" />
					</IconButton>
				</Stack>
				<Divider />
				{list.isLoading && (
					<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
						<CircularProgress size={24} />
					</Box>
				)}
				{list.data?.length === 0 && (
					<Typography color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
						{t("notifications.empty")}
					</Typography>
				)}
				{Boolean(list.data?.length) && (
					<List sx={{ px: 1 }}>
						{list.data?.map(notification => (
							<NotificationRow
								key={notification.id}
								notification={notification}
								onOpen={handleOpen}
								variant="compact"
							/>
						))}
					</List>
				)}
				<Divider />
				<Box sx={{ p: 1, textAlign: "center" }}>
					<Button component={RouterLink} to={ROUTES.activity} onClick={closePopover}>
						{t("notifications.seeAll")}
					</Button>
				</Box>
			</Popover>
		</>
	);
};
